import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Heart, MessageCircle, Send, Bookmark, Smile, BarChart3, Loader2 } from "lucide-react";
import ImageView from "../ImageView";
import CanvaView from "../CanvaView";
import { getBaseUrl } from "../../../api/api";
import { getCurrentInstagramMe } from "../../../api/AppLinkApi";
import { timeAgo } from "../../helpers";

interface Props {
  postType: string;
  content: string;
  scheduledDateTime: Date;
  mediaId: string;
  mediaType: "HOST" | "CANVA";
  engagement: any;
  onToggleInsights?: () => void;
}

export default function InstagramPost({
  content,
  scheduledDateTime,
  mediaId,
  mediaType,
  engagement,
  onToggleInsights,
}: Props) {
  const { t } = useTranslation();

  const [country, setCountry] = useState("Loading...");

  const [displayName, setDisplayName] = useState(t("Anonymous"));
  const [avatarUrl, setAvatarUrl] = useState();
  const [igHandle, setIgHandle] = useState<string>("");
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    loadProfile();
    fetch("http://ip-api.com/json/")
      .then((res) => res.json())
      .then((data) => {
        if (data.regionName && data.country)
          setCountry(`${data.regionName} - ${data.country}`);
        else
          setCountry("Unknown");
      })
      .catch(() => setCountry("Unknown"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = () => {
    getCurrentInstagramMe().then((data) => {
      setDisplayName(data?.name || t("Anonymous"));
      setAvatarUrl(data?.profilePictureUrl);
      const handle = data?.username || data?.ig_username || "";
      setIgHandle(handle ? (handle.startsWith("@") ? handle : `@${handle}`) : "");
    });
  };

  useEffect(() => {
    // Reset loading state when mediaId or mediaType changes
    if (mediaId) {
      setImageLoading(true);
      setImageError(false);
    } else {
      // For placeholder image, set loading to false immediately
      setImageLoading(false);
    }
  }, [mediaId, mediaType]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  return (
    <div className="w-full mx-auto bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-3">
          {avatarUrl ? (
            <img src={avatarUrl} alt="avatar" className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 rounded-full">
              <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-sm">
              {displayName} ·{" "}
              <span className="text-xs text-gray-500">{timeAgo(scheduledDateTime)}</span>
            </h3>
            {/* Post by @handle - Required for Facebook App Review */}
            {igHandle && (
              <p className="text-xs text-gray-500 font-medium">
                {t("Post by")} {igHandle}
              </p>
            )}
            <p className="text-xs text-gray-800">{country}</p>
          </div>
        </div>
        <button className="text-gray-600 text-xl">⋯</button>
      </div>

      {/* Post Image */}
      <div className="w-full aspect-square relative bg-gray-100 overflow-hidden">
        {imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}
        {imageError && !imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <div className="text-center text-gray-400">
              <p className="text-sm">{t("Failed to load image")}</p>
            </div>
          </div>
        )}
        {mediaId ? (
          <>
            {mediaType === "CANVA" ? (
              <div
                className={`w-full h-full ${
                  imageLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300"
                }`}
              >
                <CanvaView
                  fileId={mediaId}
                  className="w-full h-full object-contain"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              </div>
            ) : (
              <div
                className={`w-full h-full ${
                  imageLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300"
                }`}
              >
                <ImageView
                  fileName={mediaId}
                  className="w-full h-full object-contain"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              </div>
            )}
          </>
        ) : (
          <img
            src={`${getBaseUrl()}/static/images/upload-images.png`}
            alt=""
            className="w-full h-full object-cover"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center space-x-4">
          <button className="hover:scale-110 transition-transform">
            <Heart className="w-6 h-6" />
          </button>
          <span>{engagement.getIn(["metrics", "like_count"]) || 0}</span>
          <button className="hover:scale-110 transition-transform">
            <MessageCircle className="w-6 h-6" />
          </button>
          <span>{engagement.getIn(["metrics", "comments_count"]) || 0}</span>
          <button className="hover:scale-110 transition-transform">
            <Send className="w-6 h-6" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button className="hover:scale-110 transition-transform">
            <Bookmark className="w-6 h-6" />
          </button>
          {onToggleInsights && (
            <button
              onClick={onToggleInsights}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
              title={t("View Insights")}
            >
              <BarChart3 className="w-5 h-5" />
              <span className="text-sm">{t("Insights")}</span>
            </button>
          )}
        </div>
      </div>

      {/* Caption */}
      <div className="px-4 py-2 text-sm">
        <span className="font-semibold">{displayName} · </span>
        {content}
      </div>

      <div className="flex items-center justify-between px-4 py-3">
        <div className="px-4 text-sm text-gray-500">{t("Comment...")}</div>
        <Smile className="w-3 h-3" />
      </div>
    </div>
  );
}
